/**
 * "Importer fra bilde" — writer-only entry point for the photo/OCR recipe
 * import flow (Steg 5). One OR MORE photos of a (possibly multi-page) cookbook
 * recipe are staged, reordered, then sent TOGETHER to the AI vision layer and
 * come back as ONE unsaved, editor-ready DRAFT.
 *
 * REVIEW-BEFORE-SAVE: this component NEVER persists a recipe itself — it hands
 * the extracted `RecipeImportDraft` to `onImported`, which the caller uses to
 * navigate into the editor (prefilled, unsaved).
 *
 * PREMIUM, UP FRONT: photo import is a paid capability, so the button refuses
 * BEFORE opening the file picker when the active cookbook has no Premium
 * capabilities (`isPremium` — the OWNER's plan governs the cookbook, §2.1).
 * Asking the user to find and pick photos and only THEN saying "that needs
 * Premium" reads as a failure (issue #19). The button stays VISIBLE and
 * enabled on purpose — the user should learn the feature exists — it just
 * answers with the same calm `PremiumNotice` the server's 402 produces.
 * The 402 handling below REMAINS: this gate is UX, not a security boundary,
 * and the plan can change between page load and click.
 *
 * Flow: pick 1..MAX_PHOTOS images → validate + resize/re-encode each to a
 * bounded JPEG (`resizeImageForImport`) → STAGE them as a reorderable thumbnail
 * strip (order matters: page 1, 2, 3 …) → on "Importer", PUT each to its own
 * transient key (in order), start ONE async import job with all the keys, and
 * POLL until done/failed/timeout (`pollImportJob`, shared with UrlImportForm).
 * The multi-image request itself is a single Gemini call server-side.
 */

import type { RecipeImportDraft } from "@tjommi/shared";
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  EntitlementRequiredError,
  ForbiddenError,
  NotFoundError,
  UnauthorizedError,
  requestPhotoImportUpload,
  startPhotoImport,
} from "../api";
import { PremiumNotice } from "./PremiumNotice";
import { type EntitlementDenial, isEntitlementDenial } from "../lib/entitlement";
import { UPLOAD_CONTENT_TYPE, resizeImageForImport, validateImageFile } from "../lib/imageUpload";
import {
  DEFAULT_MAX_POLL_ATTEMPTS,
  DEFAULT_POLL_INTERVAL_MS,
  ImportJobFailedError,
  ImportPollCancelledError,
  ImportPollTimeoutError,
  pollImportJob,
} from "../lib/importPoll";

/** Max photos per import — mirrors the API's MAX_IMPORT_PHOTOS. */
export const MAX_PHOTOS = 6;

interface StagedImage {
  id: string;
  blob: Blob;
  /** Object URL for the thumbnail; revoked on remove/unmount. */
  url: string;
}

/** Pure reorder used by the drag handler (kept testable without the dnd runtime). */
export function reorderStaged(
  items: StagedImage[],
  activeId: string,
  overId: string,
): StagedImage[] {
  if (activeId === overId) return items;
  const from = items.findIndex((i) => i.id === activeId);
  const to = items.findIndex((i) => i.id === overId);
  if (from === -1 || to === -1) return items;
  return arrayMove(items, from, to);
}

export function PhotoImportButton({
  cookbookId,
  isPremium,
  accessToken,
  onSessionExpired,
  onImported,
  resizeImage = resizeImageForImport,
  className = "btn btn-ghost",
  busyLabel = "Leser oppskriften …",
  label = "Importer fra bilde",
  testId = "photo-import",
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  maxPollAttempts = DEFAULT_MAX_POLL_ATTEMPTS,
  onBusyChange,
}: {
  cookbookId: string;
  /**
   * Whether the ACTIVE COOKBOOK has Premium capabilities
   * (`CookbookSummary.isPremium` from `/me`, via the cookbook context). NOT
   * "is the caller premium": §2.1's locked decision is that the owner's plan
   * governs the cookbook, so a `write` member in a Premium owner's cookbook
   * may import, and a personally-Premium user in a Free owner's cookbook may
   * not. Required (no default) so a new call site cannot silently skip the
   * gate. A UI hint only — the server re-checks and answers 402.
   */
  isPremium: boolean;
  accessToken: string;
  onSessionExpired: () => void;
  /** The caller navigates into the editor with this draft (review-before-save). */
  onImported: (draft: RecipeImportDraft) => void;
  /** Injectable for tests (jsdom cannot decode real image bytes via canvas). */
  resizeImage?: (file: File) => Promise<Blob>;
  className?: string;
  busyLabel?: string;
  label?: React.ReactNode;
  testId?: string;
  /** Injectable for tests — how often to poll the job status. */
  pollIntervalMs?: number;
  /** Injectable for tests — how many polls before giving up client-side. */
  maxPollAttempts?: number;
  /** Reports whether an import is in flight, so a host sheet can refuse to
   * close (and so discard it) meanwhile. */
  onBusyChange?: (busy: boolean) => void;
}): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [staged, setStaged] = useState<StagedImage[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!busy) return;
    onBusyChange?.(true);
    return () => onBusyChange?.(false);
  }, [busy]);
  // A string (the usual short inline error) OR a denial — either the parsed
  // 402 or the client's own up-front `isPremium` refusal. One state keeps them
  // mutually exclusive.
  const [error, setError] = useState<string | EntitlementDenial | null>(null);

  // A run token: bumped on each import; a stale poll loop must not call
  // setState/onImported after an unmount.
  const runIdRef = useRef(0);
  const mountedRef = useRef(true);
  // Mirror `staged` in a ref so the unmount cleanup revokes the CURRENT urls
  // without re-subscribing the effect on every change.
  const stagedRef = useRef<StagedImage[]>([]);
  stagedRef.current = staged;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      for (const s of stagedRef.current) URL.revokeObjectURL(s.url);
    };
  }, []);

  /**
   * A cookbook switch must not leave the previous cookbook's answer on screen.
   * `HomePage` keeps the SAME component instance across a switch (no `key`),
   * so without this the Premium notice from a Free cookbook would still sit
   * under a button that now works — and staged photos picked for cookbook A
   * would be uploadable into cookbook B. `isPremium` is in the deps too: a
   * `/me` refresh that flips the plan is the same situation.
   */
  useEffect(() => {
    setError(null);
    // `clearStaged` only touches refs + state, so it is intentionally NOT a
    // dependency: re-running this on its identity would wipe a fresh pick.
    clearStaged();
  }, [cookbookId, isPremium]);

  /**
   * Opens the file picker. Belt AND braces with the conditional render below:
   * without Premium there is no input to click in the first place, and this
   * early return is what turns the click into the notice. (An earlier version
   * of this comment claimed to be "the ONE way the picker is opened" — it
   * wasn't: the input was rendered unconditionally and was reachable by TAB,
   * bypassing this function entirely. See the render block.)
   */
  function openPicker(): void {
    if (!isPremium) {
      setError({ code: "photo_import" });
      return;
    }
    setError(null);
    inputRef.current?.click();
  }

  const addFiles = useCallback(
    async (files: File[]): Promise<void> => {
      setError(null);
      const room = MAX_PHOTOS - stagedRef.current.length;
      if (room <= 0) {
        setError(`Du kan ha maks ${MAX_PHOTOS} bilder.`);
        return;
      }
      const take = files.slice(0, room);
      if (files.length > room) {
        setError(`Maks ${MAX_PHOTOS} bilder — tok med de ${room} første.`);
      }
      for (const file of take) {
        const validationError = validateImageFile(file);
        if (validationError) {
          setError(validationError);
          continue;
        }
        try {
          const blob = await resizeImage(file);
          if (!mountedRef.current) return;
          setStaged((prev) => [
            ...prev,
            { id: crypto.randomUUID(), blob, url: URL.createObjectURL(blob) },
          ]);
        } catch {
          setError("Kunne ikke lese bildet. Prøv et annet.");
        }
      }
    },
    [resizeImage],
  );

  function removeStaged(id: string): void {
    setStaged((prev) => {
      const gone = prev.find((s) => s.id === id);
      if (gone) URL.revokeObjectURL(gone.url);
      return prev.filter((s) => s.id !== id);
    });
  }

  function clearStaged(): void {
    for (const s of stagedRef.current) URL.revokeObjectURL(s.url);
    setStaged([]);
  }

  function onDragEnd(e: DragEndEvent): void {
    const overId = e.over?.id;
    if (overId) setStaged((prev) => reorderStaged(prev, String(e.active.id), String(overId)));
  }

  const doImport = useCallback(async (): Promise<void> => {
    if (stagedRef.current.length === 0 || busy) return;
    setError(null);
    const runId = ++runIdRef.current;
    const isActive = () => mountedRef.current && runIdRef.current === runId;
    setBusy(true);
    try {
      const keys: string[] = [];
      for (const s of stagedRef.current) {
        const { key, uploadUrl } = await requestPhotoImportUpload(
          cookbookId,
          UPLOAD_CONTENT_TYPE,
          accessToken,
        );
        const putRes = await fetch(uploadUrl, {
          method: "PUT",
          body: s.blob,
          headers: { "Content-Type": UPLOAD_CONTENT_TYPE },
        });
        if (!putRes.ok) {
          setError("Opplasting feilet. Prøv igjen.");
          return;
        }
        keys.push(key);
      }
      const { jobId } = await startPhotoImport(cookbookId, keys, accessToken);
      const draft = await pollImportJob(cookbookId, jobId, accessToken, {
        intervalMs: pollIntervalMs,
        maxAttempts: maxPollAttempts,
        isActive,
      });
      if (isActive()) {
        clearStaged();
        onImported(draft);
      }
    } catch (err) {
      if (err instanceof ImportPollCancelledError) return;
      if (err instanceof UnauthorizedError) return onSessionExpired();
      if (err instanceof ForbiddenError) return setError("Ingen skrivetilgang.");
      // 402: photo import isn't in the cookbook's plan (either the upload-url
      // call or the start call can answer this). NOT the same as the 403 above.
      if (err instanceof EntitlementRequiredError) return setError(err);
      if (err instanceof NotFoundError)
        return setError("Importen kunne ikke finnes igjen. Prøv igjen.");
      if (err instanceof ImportJobFailedError)
        return setError(
          err.serverMessage ??
            "Klarte ikke å lese oppskriften fra bildene — prøv tydeligere bilder.",
        );
      if (err instanceof ImportPollTimeoutError)
        return setError("Importen tok for lang tid. Prøv igjen.");
      setError(err instanceof Error ? err.message : "Noe gikk galt. Prøv igjen.");
    } finally {
      if (mountedRef.current && runIdRef.current === runId) setBusy(false);
    }
  }, [
    accessToken,
    busy,
    cookbookId,
    maxPollAttempts,
    onImported,
    onSessionExpired,
    pollIntervalMs,
  ]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-picking the same file(s)
    if (files.length > 0) void addFiles(files);
  }

  const canAddMore = staged.length < MAX_PHOTOS;

  return (
    <span className="photo-import">
      {staged.length === 0 ? (
        <button
          type="button"
          className={className}
          data-testid={testId}
          disabled={busy}
          onClick={openPicker}
        >
          {label}
        </button>
      ) : (
        <div className="photo-stage" data-testid={`${testId}-stage`}>
          <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={staged.map((s) => s.id)}
              strategy={horizontalListSortingStrategy}
            >
              <div className="photo-stage-strip">
                {staged.map((s, i) => (
                  <StagedThumb
                    key={s.id}
                    image={s}
                    index={i}
                    testId={testId}
                    disabled={busy}
                    onRemove={() => removeStaged(s.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <div className="photo-stage-actions">
            <button
              type="button"
              className="btn btn-primary"
              data-testid={`${testId}-confirm`}
              disabled={busy}
              onClick={() => void doImport()}
            >
              {busy
                ? busyLabel
                : `Importer ${staged.length} ${staged.length === 1 ? "bilde" : "bilder"}`}
            </button>
            {canAddMore && (
              <button
                type="button"
                className="btn btn-ghost"
                data-testid={`${testId}-add-more`}
                disabled={busy}
                onClick={openPicker}
              >
                Legg til flere
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost"
              data-testid={`${testId}-cancel`}
              disabled={busy}
              onClick={clearStaged}
            >
              Avbryt
            </button>
          </div>
        </div>
      )}
      {/* THE FILE INPUT ONLY EXISTS WITH PREMIUM (issue #19, M1).
          `visually-hidden` is CLIP-based, not `display: none`, so an
          unconditionally rendered input stays in the TAB ORDER and in the
          accessibility tree: a keyboard or screen-reader user could tab past
          the button straight onto it, open the native picker, stage photos and
          only meet the refusal at "Importer N bilder" — the exact bug #19 is
          about, just for people who don't use a mouse. Not rendering it at all
          removes the path (mirrors PDF/video, whose inputs only exist inside
          their modal) rather than merely hiding it; `tabIndex={-1}` +
          `aria-hidden` would still let the picker open for anyone who reaches
          it another way, which is still "do the work first". */}
      {isPremium && (
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="visually-hidden"
          data-testid={`${testId}-file-input`}
          onChange={handleChange}
        />
      )}
      {isEntitlementDenial(error) ? (
        <PremiumNotice denial={error} testId={`${testId}-premium`} />
      ) : typeof error === "string" ? (
        <p className="image-error" data-testid={`${testId}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </span>
  );
}

function StagedThumb({
  image,
  index,
  testId,
  disabled,
  onRemove,
}: {
  image: StagedImage;
  index: number;
  testId: string;
  disabled: boolean;
  onRemove: () => void;
}): React.JSX.Element {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: image.id,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`photo-thumb${isDragging ? " dragging" : ""}`}
      data-testid={`${testId}-thumb-${index}`}
    >
      <button
        type="button"
        className="photo-thumb-grip"
        aria-label={`Flytt bilde ${index + 1}`}
        data-testid={`${testId}-grip-${index}`}
        {...attributes}
        {...listeners}
      >
        <img src={image.url} alt="" />
        <span className="photo-thumb-n" aria-hidden="true">
          {index + 1}
        </span>
      </button>
      <button
        type="button"
        className="photo-thumb-x"
        aria-label={`Fjern bilde ${index + 1}`}
        data-testid={`${testId}-remove-${index}`}
        disabled={disabled}
        onClick={onRemove}
      >
        ✕
      </button>
    </div>
  );
}
